/**
 * AudioPlayer Component
 *
 * Audio player with waveform visualization using wavesurfer.js
 *
 * Features:
 * - Waveform visualization
 * - Play/pause controls
 * - Progress tracking
 * - Time display
 * - Volume control
 * - Speed control
 * - Seek functionality
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import type WaveSurferType from 'wavesurfer.js';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward } from 'lucide-react';
import { readDesignTokens } from '@/lib/clerk-theme';
import { logDebug } from '@/lib/logger';
import { subscribeTheme } from '@/lib/theme-subscribe';
import { cn } from '@/lib/utils';
import { Select } from '@/components/ui/select';
import { Skeleton, SkeletonMedia } from '@/components/ui/skeleton';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

// Dynamic import for wavesurfer.js (~150KB) - only loaded when AudioPlayer is used
const loadWaveSurfer = () => import('wavesurfer.js').then((m) => m.default);

/**
 * Recordings longer than this skip waveform decoding entirely.
 *
 * The WebAudio backend decodes the WHOLE file into Float32 PCM (a 1-hour
 * stereo call is >1 GB of samples; even 5 minutes is ~115 MB) and computes
 * peaks over every sample on the main thread — seconds of blocking + a GC
 * storm per record click. Long recordings stream via the MediaElement
 * backend with placeholder peaks instead; all controls keep working, only
 * the real waveform shape is lost.
 *
 * Records with UNKNOWN duration (metadata not yet backfilled by ASR, or a
 * connector that didn't report it) are treated as long — fail closed: the
 * dangerous case is exactly the one without metadata.
 */
export const WAVEFORM_DECODE_LIMIT_SECONDS = 300;

// Flat placeholder rendered instead of the real waveform for long recordings.
const PLACEHOLDER_PEAKS = [new Array<number>(512).fill(0.18)];

/**
 * The waveform is drawn on a canvas, and a canvas cannot resolve CSS
 * variables: a `var(--color-…)` colour fell back to black in both themes
 * (#1178). Token VALUES, read the way Clerk reads them — with palette
 * fallbacks while the stylesheet is not applied yet, since an empty colour
 * would paint black again. Unplayed bars are `fg-subtle`: the waveform carries
 * meaning, and the decorative `fg-faint` is under 3:1 on the player. The
 * cursor takes the progress colour by itself.
 */
function waveformColors() {
  const tokens = readDesignTokens();
  return { waveColor: tokens['color-fg-subtle'], progressColor: tokens['color-primary'] };
}

/** Normalised peaks reach the full height; this keeps them off the edges. */
const WAVEFORM_BAR_HEIGHT = 0.85;

/**
 * wavesurfer normalises by channel 0 alone but draws channel 1 below the axis:
 * on a stereo call whose second leg is louder, those bars run past the edge.
 * Scale the bar height down by how much louder the loudest channel is.
 */
export function stereoSafeBarHeight(channels: Float32Array[], target: number): number {
  // One plain pass per channel: a 5-minute stereo call at 48 kHz is ~29M
  // samples, read on the main thread right after decoding.
  const peaks = channels.map((data) => {
    let max = 0;
    for (let i = 0; i < data.length; i++) {
      const v = data[i]! < 0 ? -data[i]! : data[i]!;
      if (v > max) max = v;
    }
    return max;
  });
  const [first = 0] = peaks;
  const loudest = Math.max(first, ...peaks);
  return loudest > first && first > 0 ? (target * first) / loudest : target;
}

interface WaveSurferHandlers {
  ready: () => void;
  error: (error: Error) => void;
  play: () => void;
  pause: () => void;
  audioprocess: () => void;
  seeking: (currentTime: number) => void;
  finish: () => void;
}

function subscribeToWaveSurfer(
  wavesurfer: WaveSurferType,
  handlers: WaveSurferHandlers
): () => void {
  const unsubscribers = [
    wavesurfer.on('ready', handlers.ready),
    wavesurfer.on('error', handlers.error),
    wavesurfer.on('play', handlers.play),
    wavesurfer.on('pause', handlers.pause),
    wavesurfer.on('audioprocess', handlers.audioprocess),
    wavesurfer.on('seeking', handlers.seeking),
    wavesurfer.on('finish', handlers.finish),
  ];

  return () => {
    for (const unsubscribe of unsubscribers) {
      unsubscribe?.();
    }
  };
}

export interface AudioPlayerProps {
  audioUrl: string;
  /**
   * Known recording duration from record metadata. Recordings longer than
   * WAVEFORM_DECODE_LIMIT_SECONDS are streamed without waveform decoding.
   */
  durationSeconds?: number;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationLoaded?: (durationSeconds: number) => void;
  className?: string;
  /** Compact single-line mode: hides waveform, shows slim progress bar */
  compact?: boolean;
  /**
   * The opening a link asked for (#2608): the cursor at `at` once the audio is
   * ready, without playing. It is an intent, not a value (#2966): the player
   * applies a key once, and the key it applied last, handed again, changes
   * nothing, whatever the player is doing. A new key for audio already decoded
   * stops playback and the fragment, and moves the cursor when `at` is inside
   * the recording; a key without `at` (the moment taken off the link) only
   * stops. A segment queued before `ready` wins and spends the moment; a moment
   * outside the recording leaves the cursor where it is. A key waits while the
   * recording's duration is not known.
   */
  linkMoment?: AudioPlayerLinkMoment;
  /**
   * A key of `linkMoment` is spent. A host that unmounts the player (tabs)
   * stops handing it that key, so a remounted player does not jump back.
   */
  onLinkMomentApplied?: (key: string) => void;
  /**
   * Where the cursor stands once the audio is ready when no opening is pending:
   * the listener's place before the host unmounted the player (tabs), so coming
   * back does not start the recording over (#2966). Read in `ready` only.
   */
  resumeAtSeconds?: number;
}

/** One link's request to open the recording at a moment — see `linkMoment`. */
export interface AudioPlayerLinkMoment {
  key: string;
  at?: number;
}

export interface AudioPlayerHandle {
  seekAndPlay: (timeInSeconds: number) => void;
  playSegment: (startSeconds: number, durationSeconds?: number) => Promise<void>;
}

interface ActiveSegment {
  startSeconds: number;
  endSeconds: number;
  atEnd: boolean;
}

const DEFAULT_SEGMENT_DURATION_SECONDS = 20;

/** The cursor fraction for an initial position, or `null` outside the recording. */
function initialPositionFraction(
  atSeconds: number,
  mediaDuration: number
): number | null {
  if (!Number.isFinite(atSeconds) || !Number.isFinite(mediaDuration)) return null;
  if (atSeconds < 0 || atSeconds >= mediaDuration) return null;
  return atSeconds / mediaDuration;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(
  (
    {
      audioUrl,
      durationSeconds,
      onTimeUpdate,
      onDurationLoaded,
      className,
      compact = false,
      linkMoment,
      onLinkMomentApplied,
      resumeAtSeconds,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const waveformRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurferType | null>(null);
    const mediaDurationRef = useRef(0);
    const segmentRef = useRef<ActiveSegment | null>(null);
    const queuedSegmentRef = useRef<{
      audioUrl: string;
      startSeconds: number;
      durationSeconds: number;
    } | null>(null);
    const startSegmentRef = useRef<
      (
        wavesurfer: WaveSurferType,
        mediaDuration: number,
        startSeconds: number,
        segmentDurationSeconds: number
      ) => Promise<void>
    >(async () => {});

    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.7);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [activeSegment, setActiveSegment] = useState<ActiveSegment | null>(null);

    // Stable refs for callbacks and i18n to avoid re-creating WaveSurfer on every render
    const onTimeUpdateRef = useRef(onTimeUpdate);
    const onDurationLoadedRef = useRef(onDurationLoaded);
    const tRef = useRef(t);
    // Read via ref inside the init effect so the effect stays keyed on
    // audioUrl ALONE. With durationSeconds in the deps, a record switch
    // re-ran the effect while audioUrl still pointed at the PREVIOUS
    // record's blob (record comes synchronously from the query cache,
    // the blob URL arrives later) — recreating WaveSurfer with a stale
    // long-recording URL and the new record's short duration re-triggered
    // the full-PCM decode this gate exists to prevent.
    const durationSecondsRef = useRef(durationSeconds);
    // Read in `ready` for the same reason: a new moment must not reload audio.
    const linkMomentRef = useRef(linkMoment);
    const onLinkMomentAppliedRef = useRef(onLinkMomentApplied);
    const resumeAtRef = useRef(resumeAtSeconds);
    // The last key this player applied: a key is an opening, applied once.
    const appliedLinkKeyRef = useRef<string | null>(null);
    useLayoutEffect(() => {
      onTimeUpdateRef.current = onTimeUpdate;
      onDurationLoadedRef.current = onDurationLoaded;
      tRef.current = t;
      durationSecondsRef.current = durationSeconds;
      linkMomentRef.current = linkMoment;
      onLinkMomentAppliedRef.current = onLinkMomentApplied;
      resumeAtRef.current = resumeAtSeconds;
    }, [
      onTimeUpdate,
      onDurationLoaded,
      t,
      durationSeconds,
      linkMoment,
      onLinkMomentApplied,
      resumeAtSeconds,
    ]);

    /**
     * The link's opening not yet applied by this player, marked as applied now
     * — or `null`, also while the duration it is applied against is unknown:
     * spent then, it would be lost without a seek.
     */
    const takeLinkMoment = useCallback(
      (mediaDuration: number): AudioPlayerLinkMoment | null => {
        if (!Number.isFinite(mediaDuration) || mediaDuration <= 0) return null;
        const moment = linkMomentRef.current;
        if (!moment || moment.key === appliedLinkKeyRef.current) return null;
        appliedLinkKeyRef.current = moment.key;
        onLinkMomentAppliedRef.current?.(moment.key);
        return moment;
      },
      []
    );

    /** Puts the cursor at `at`, without playing, when it is inside the recording. */
    const moveCursorTo = useCallback(
      (wavesurfer: WaveSurferType, at: number | undefined, mediaDuration: number) => {
        if (at === undefined) return;
        const fraction = initialPositionFraction(at, mediaDuration);
        if (fraction === null) return;
        wavesurfer.seekTo(fraction);
        setCurrentTime(at);
        onTimeUpdateRef.current?.(at);
      },
      []
    );

    // Suppress AbortError from WaveSurfer's internal fetch
    useEffect(() => {
      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        // Check if it's an AbortError from WaveSurfer
        if (event.reason?.name === 'AbortError') {
          // Prevent the error from appearing in console
          event.preventDefault();
          logDebug('audio_player.abort_error_suppressed');
        }
      };

      window.addEventListener('unhandledrejection', handleUnhandledRejection);

      return () => {
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      };
    }, []);

    // Initialize WaveSurfer with dynamic import
    useEffect(() => {
      if (!waveformRef.current) return;

      // Reset state on URL change
      setIsLoading(true);
      setLoadError(null);
      setCurrentTime(0);
      setDuration(0);
      setActiveSegment(null);
      wavesurferRef.current = null;
      mediaDurationRef.current = 0;
      segmentRef.current = null;
      // A queued segment never survives a re-init: the previous effect's
      // cleanup already cleared it, and a request for the NEW url can only
      // arrive after this effect has run (child effects run before the
      // parent's). Cleared unconditionally so the mount path matches.
      queuedSegmentRef.current = null;

      let wavesurfer: WaveSurferType | null = null;
      let unsubscribeWaveSurferEvents: (() => void) | null = null;
      let unsubscribeTheme: (() => void) | null = null;
      let appliedColors = waveformColors();
      let isCancelled = false;

      // Long or UNKNOWN-duration recordings: stream via MediaElement with
      // placeholder peaks — decoding them into PCM froze the tab (see
      // WAVEFORM_DECODE_LIMIT_SECONDS). Fail closed: no metadata = no decode.
      const knownDuration = durationSecondsRef.current;
      const skipWaveformDecode =
        knownDuration == null || knownDuration > WAVEFORM_DECODE_LIMIT_SECONDS;

      // Dynamic import - only loads wavesurfer.js when AudioPlayer is mounted.
      // Keep the try/catch inside the effect-owned initializer so a chunk-load
      // failure cannot leave the player spinning or surface unhandled.
      async function initializeWaveSurfer() {
        try {
          const WaveSurfer = await loadWaveSurfer();
          if (isCancelled || !waveformRef.current) return;
          // Read after the chunk arrived: a theme switch during the download
          // would otherwise paint the old palette until the next switch.
          appliedColors = waveformColors();

          wavesurfer = WaveSurfer.create({
            container: waveformRef.current,
            ...appliedColors,
            // Headroom only for normalised peaks: the placeholder keeps its height.
            ...(skipWaveformDecode ? {} : { barHeight: WAVEFORM_BAR_HEIGHT }),
            barWidth: 2,
            barRadius: 3,
            cursorWidth: 1,
            height: 80,
            barGap: 2,
            // normalize rescales peaks by 1/max — constant placeholder peaks
            // (0.18) would render as FULL-height bars. Off for the gated path.
            normalize: !skipWaveformDecode,
            ...(skipWaveformDecode ? {} : { backend: 'WebAudio' as const }),
          });

          wavesurferRef.current = wavesurfer;

          // A palette switch changes the token values, which the canvas only
          // learns from new options — and only a real change is worth a redraw.
          unsubscribeTheme = subscribeTheme(() => {
            const next = waveformColors();
            if (
              next.waveColor === appliedColors.waveColor &&
              next.progressColor === appliedColors.progressColor
            ) {
              return;
            }
            appliedColors = next;
            wavesurfer?.setOptions(next);
          });

          if (!skipWaveformDecode) {
            wavesurfer.on('decode', () => {
              const data = wavesurfer?.getDecodedData();
              if (!data || data.numberOfChannels < 2) return;
              const channels = Array.from({ length: data.numberOfChannels }, (_, i) =>
                data.getChannelData(i)
              );
              wavesurfer?.setOptions({
                barHeight: stereoSafeBarHeight(channels, WAVEFORM_BAR_HEIGHT),
              });
            });
          }

          // Load audio. Providing channelData makes wavesurfer skip the
          // full-file fetch and decodeAudioData entirely. Duration is taken
          // from the media element's metadata (NOT from record metadata):
          // that way 'ready' fires only once the source is actually reachable
          // and seekable, instead of synchronously from provided peaks.
          // wavesurfer v7 aborts the in-flight fetch inside destroy() (on unmount
          // or audioUrl change mid-load), which rejects load() with AbortError.
          // That rejection is async, so the cleanup try/catch can't see it —
          // without a catch here it surfaces as an unhandled rejection in Sentry.
          // Genuine load failures already arrive via the 'error' event, so just
          // swallow aborts/cancellations and debug-log anything unexpected.
          const handleLoadRejection = (error: unknown) => {
            const name = error instanceof Error ? error.name : '';
            if (isCancelled || name === 'AbortError') return;
            logDebug('audio_player.load_rejected', {
              error: error instanceof Error ? error.message : String(error),
              audioUrl,
            });
          };

          if (skipWaveformDecode) {
            wavesurfer.load(audioUrl, PLACEHOLDER_PEAKS).catch(handleLoadRejection);
          } else {
            wavesurfer.load(audioUrl).catch(handleLoadRejection);
          }

          unsubscribeWaveSurferEvents = subscribeToWaveSurfer(wavesurfer, {
            ready: () => {
              setIsLoading(false);
              setLoadError(null);
              const dur = wavesurfer!.getDuration();
              setDuration(dur);
              mediaDurationRef.current = dur;
              onDurationLoadedRef.current?.(Math.round(dur));

              const moment = takeLinkMoment(dur);
              const queuedSegment = queuedSegmentRef.current;
              if (queuedSegment?.audioUrl === audioUrl) {
                queuedSegmentRef.current = null;
                void startSegmentRef.current(
                  wavesurfer!,
                  dur,
                  queuedSegment.startSeconds,
                  queuedSegment.durationSeconds
                );
                return;
              }
              moveCursorTo(wavesurfer!, moment?.at ?? resumeAtRef.current, dur);
            },
            error: (error: Error) => {
              logDebug('audio_player.load_error', { error: error.message, audioUrl });
              setIsLoading(false);
              setLoadError(error.message || tRef.current('audioPlayer.failedToLoad'));
            },
            play: () => setIsPlaying(true),
            pause: () => setIsPlaying(false),
            audioprocess: () => {
              const time = wavesurfer!.getCurrentTime();
              setCurrentTime(time);
              onTimeUpdateRef.current?.(time);

              const segment = segmentRef.current;
              if (segment && !segment.atEnd && time >= segment.endSeconds) {
                const completedSegment = { ...segment, atEnd: true };
                segmentRef.current = completedSegment;
                setActiveSegment(completedSegment);
                wavesurfer!.pause();
                wavesurfer!.seekTo(
                  completedSegment.endSeconds / wavesurfer!.getDuration()
                );
                setCurrentTime(completedSegment.endSeconds);
                onTimeUpdateRef.current?.(completedSegment.endSeconds);
              }
            },
            // A seek while paused (the waveform, the skip buttons) moves no
            // `audioprocess`: the host learns the place from here, so the
            // transcript and a player brought back by the tabs follow it.
            seeking: (time: number) => {
              setCurrentTime(time);
              onTimeUpdateRef.current?.(time);
            },
            finish: () => {
              setIsPlaying(false);
              const segment = segmentRef.current;
              const time = segment ? segment.endSeconds : 0;
              if (segment) {
                const completedSegment = { ...segment, atEnd: true };
                segmentRef.current = completedSegment;
                setActiveSegment(completedSegment);
              }
              setCurrentTime(time);
              onTimeUpdateRef.current?.(time);
            },
          });
        } catch (error) {
          if (isCancelled) return;
          logDebug('audio_player.module_load_rejected', {
            error: error instanceof Error ? error.message : String(error),
          });
          setIsLoading(false);
          setLoadError(tRef.current('audioPlayer.failedToLoad'));
        }
      }

      void initializeWaveSurfer();

      return () => {
        isCancelled = true;
        unsubscribeTheme?.();
        wavesurferRef.current = null;
        mediaDurationRef.current = 0;
        segmentRef.current = null;
        queuedSegmentRef.current = null;
        try {
          unsubscribeWaveSurferEvents?.();
          if (wavesurfer) {
            // Stop playback before destroying
            if (wavesurfer.isPlaying()) {
              wavesurfer.stop();
            }

            // Unsubscribe from all events to prevent errors
            wavesurfer.unAll();

            // Destroy WaveSurfer instance
            wavesurfer.destroy();
          }
        } catch (error) {
          // Ignore errors during cleanup
          logDebug('audio_player.cleanup_error', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      };
      // Only re-create WaveSurfer when the audio source changes.
      // durationSeconds is read via ref (see durationSecondsRef above);
      // callbacks (onTimeUpdate, onDurationLoaded) and t use stable refs too;
      // takeLinkMoment and moveCursorTo never change (they read refs).
    }, [audioUrl, takeLinkMoment, moveCursorTo]);

    // Update volume
    useEffect(() => {
      if (wavesurferRef.current) {
        wavesurferRef.current.setVolume(isMuted ? 0 : volume);
      }
    }, [volume, isMuted]);

    // Update playback rate
    useEffect(() => {
      if (wavesurferRef.current) {
        wavesurferRef.current.setPlaybackRate(playbackRate);
      }
    }, [playbackRate]);

    const togglePlayPause = useCallback(() => {
      const wavesurfer = wavesurferRef.current;
      if (!wavesurfer) return;

      const segment = segmentRef.current;
      if (segment?.atEnd) {
        const replayingSegment = { ...segment, atEnd: false };
        segmentRef.current = replayingSegment;
        setActiveSegment(replayingSegment);
        wavesurfer.seekTo(replayingSegment.startSeconds / mediaDurationRef.current);
        setCurrentTime(replayingSegment.startSeconds);
        void wavesurfer.play();
        return;
      }

      void wavesurfer.playPause();
    }, []);

    const toggleMute = useCallback(() => {
      setIsMuted((prev) => !prev);
    }, []);

    const skipBackward = useCallback(() => {
      if (wavesurferRef.current && duration > 0) {
        segmentRef.current = null;
        setActiveSegment(null);
        const newTime = Math.max(0, currentTime - 10);
        wavesurferRef.current.seekTo(newTime / duration);
      }
    }, [currentTime, duration]);

    const skipForward = useCallback(() => {
      if (wavesurferRef.current && duration > 0) {
        segmentRef.current = null;
        setActiveSegment(null);
        const newTime = Math.min(duration, currentTime + 10);
        wavesurferRef.current.seekTo(newTime / duration);
      }
    }, [currentTime, duration]);

    const seekAndPlay = useCallback(
      (timeInSeconds: number) => {
        if (wavesurferRef.current && duration > 0) {
          segmentRef.current = null;
          setActiveSegment(null);
          // Seek to the specified time
          const clampedTime = Math.max(0, Math.min(timeInSeconds, duration));
          wavesurferRef.current.seekTo(clampedTime / duration);
          // Start playing
          void wavesurferRef.current.play();
        }
      },
      [duration]
    );

    // A new opening for audio that is already decoded (the same record opened
    // again in place) stops playback and the fragment now and moves the cursor
    // when its moment is inside the recording; audio still loading takes it in
    // `ready`. Keyed on the opening, not on the moment: the same moment opened
    // again is a new key, and a re-render with the old key is nothing.
    const linkMomentKey = linkMoment?.key;
    useEffect(() => {
      const wavesurfer = wavesurferRef.current;
      if (!wavesurfer) return;
      const mediaDuration = mediaDurationRef.current;
      const moment = takeLinkMoment(mediaDuration);
      if (!moment) return;
      segmentRef.current = null;
      setActiveSegment(null);
      if (wavesurfer.isPlaying()) wavesurfer.pause();
      moveCursorTo(wavesurfer, moment.at, mediaDuration);
    }, [linkMomentKey, takeLinkMoment, moveCursorTo]);

    const startSegment = useCallback(
      async (
        wavesurfer: WaveSurferType,
        mediaDuration: number,
        startSeconds: number,
        segmentDurationSeconds: number
      ) => {
        if (!Number.isFinite(mediaDuration) || mediaDuration <= 0) return;

        const clampedStart = Math.max(0, Math.min(startSeconds, mediaDuration));
        const safeSegmentDuration =
          Number.isFinite(segmentDurationSeconds) && segmentDurationSeconds > 0
            ? segmentDurationSeconds
            : DEFAULT_SEGMENT_DURATION_SECONDS;
        const segment: ActiveSegment = {
          startSeconds: clampedStart,
          endSeconds: Math.min(mediaDuration, clampedStart + safeSegmentDuration),
          atEnd: false,
        };

        segmentRef.current = segment;
        setActiveSegment(segment);
        wavesurfer.seekTo(clampedStart / mediaDuration);
        setCurrentTime(clampedStart);
        onTimeUpdateRef.current?.(clampedStart);
        try {
          await wavesurfer.play();
        } catch (error) {
          logDebug('audio_player.segment_play_rejected', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      },
      []
    );

    useLayoutEffect(() => {
      startSegmentRef.current = startSegment;
    }, [startSegment]);

    const playSegment = useCallback(
      async (
        startSeconds: number,
        segmentDurationSeconds = DEFAULT_SEGMENT_DURATION_SECONDS
      ) => {
        const safeStart = Number.isFinite(startSeconds) ? Math.max(0, startSeconds) : 0;
        const request = {
          audioUrl,
          startSeconds: safeStart,
          durationSeconds: segmentDurationSeconds,
        };
        queuedSegmentRef.current = request;

        const wavesurfer = wavesurferRef.current;
        const mediaDuration = mediaDurationRef.current;
        if (!wavesurfer || mediaDuration <= 0) return;

        queuedSegmentRef.current = null;
        await startSegment(
          wavesurfer,
          mediaDuration,
          request.startSeconds,
          request.durationSeconds
        );
      },
      [audioUrl, startSegment]
    );

    const continuePlayback = useCallback(() => {
      const wavesurfer = wavesurferRef.current;
      const segment = segmentRef.current;
      segmentRef.current = null;
      queuedSegmentRef.current = null;
      setActiveSegment(null);

      if (wavesurfer && segment?.atEnd) {
        void wavesurfer.play();
      }
    }, []);

    // Expose playback controls to transcript, key moments and deep links.
    useImperativeHandle(
      ref,
      () => ({
        seekAndPlay,
        playSegment,
      }),
      [seekAndPlay, playSegment]
    );

    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Progress percentage for compact progress bar
    const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Handle click on compact progress bar to seek
    const handleProgressClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!duration || isLoading || loadError) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pct = Math.max(0, Math.min(1, x / rect.width));
        segmentRef.current = null;
        setActiveSegment(null);
        wavesurferRef.current?.seekTo(pct);
      },
      [duration, isLoading, loadError]
    );

    return (
      <div
        data-testid="audio-player"
        className={cn(compact ? 'flex items-center gap-3' : 'space-y-4', className)}
      >
        {/* Waveform — ALWAYS the same DOM node across compact/full toggles
            and error states. WaveSurfer binds to this node once per audioUrl;
            rendering it per-branch left the instance bound to a detached
            element after a mode switch (blank waveform, dead click-to-seek),
            and unmounting it on loadError made recovery on the next record
            impossible (init effect bails when waveformRef.current is null). */}
        <div
          data-testid="audio-player-waveform-container"
          className={cn('relative min-h-[80px]', compact && 'hidden')}
        >
          {!compact && isLoading && !loadError && (
            <div data-testid="audio-player-loading" className="absolute inset-0">
              <SkeletonMedia aspectRatio="16/9" />
            </div>
          )}
          {!compact && loadError && (
            <div
              data-testid="audio-player-error"
              className="flex items-center justify-center h-20 bg-danger-dim border border-danger rounded-card text-danger-fg text-sm"
            >
              <span>
                {t('audioPlayer.failedToLoad')}: {loadError}
              </span>
            </div>
          )}
          <div
            ref={waveformRef}
            data-testid="audio-player-waveform"
            className={cn('rounded-card overflow-hidden', loadError && 'hidden')}
          />
        </div>

        {compact ? (
          <>
            {/* Play/Pause */}
            <button
              data-testid="audio-player-play-pause"
              onClick={togglePlayPause}
              disabled={isLoading || !!loadError}
              className={cn(
                CONTROL_SQUARE.sm,
                'flex shrink-0 items-center justify-center rounded-button bg-primary text-bg hover:bg-primary-hover transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4 ml-0.5" />
              )}
            </button>

            {/* Skip backward */}
            <button
              data-testid="audio-player-skip-backward"
              onClick={skipBackward}
              disabled={isLoading || !!loadError}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-button text-fg-muted hover:text-fg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('accessibility.audioPlayer.skipBackward')}
            >
              <SkipBack className="h-3.5 w-3.5" />
            </button>

            {/* Time */}
            <span className="text-xs text-fg-subtle font-mono shrink-0 tabular-nums">
              {formatTime(currentTime)}
            </span>

            {/* Slim progress bar */}
            <div
              data-testid="audio-player-progress"
              className="flex-1 h-1.5 bg-bg-subtle rounded-full cursor-pointer relative"
              onClick={handleProgressClick}
            >
              <div
                className="absolute inset-y-0 left-0 bg-primary rounded-full transition-[width] duration-100"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Duration */}
            <span className="text-xs text-fg-subtle font-mono shrink-0 tabular-nums">
              {formatTime(duration)}
            </span>

            {/* Skip forward */}
            <button
              data-testid="audio-player-skip-forward"
              onClick={skipForward}
              disabled={isLoading || !!loadError}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-button text-fg-muted hover:text-fg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('accessibility.audioPlayer.skipForward')}
            >
              <SkipForward className="h-3.5 w-3.5" />
            </button>

            {/* Speed (compact) */}
            <Select
              data-testid="audio-player-speed-select"
              value={String(playbackRate)}
              onValueChange={(val) => setPlaybackRate(Number(val))}
              disabled={isLoading}
              options={[
                { value: '0.5', label: '0.5x' },
                { value: '0.75', label: '0.75x' },
                { value: '1', label: '1x' },
                { value: '1.25', label: '1.25x' },
                { value: '1.5', label: '1.5x' },
                { value: '2', label: '2x' },
              ]}
              size="sm"
              className="w-[68px]"
            />

            {/* Mute toggle (no slider in compact) */}
            <button
              data-testid="audio-player-mute-button"
              onClick={toggleMute}
              disabled={isLoading || !!loadError}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-button text-fg-muted hover:text-fg transition-colors disabled:opacity-50"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeX className="h-3.5 w-3.5" />
              ) : (
                <Volume2 className="h-3.5 w-3.5" />
              )}
            </button>
          </>
        ) : (
          <div data-testid="audio-player-controls" className="flex items-center gap-4">
            {/* Playback buttons */}
            <div
              data-testid="audio-player-playback-buttons"
              className="flex items-center gap-2"
            >
              <button
                data-testid="audio-player-skip-backward"
                onClick={skipBackward}
                disabled={isLoading || !!loadError}
                className={cn(
                  CONTROL_SQUARE.sm,
                  'flex items-center justify-center rounded-button text-fg-muted hover:bg-bg-elevated hover:text-fg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                aria-label={t('accessibility.audioPlayer.skipBackward')}
              >
                <SkipBack className="h-4 w-4" />
              </button>

              <button
                data-testid="audio-player-play-pause"
                onClick={togglePlayPause}
                disabled={isLoading || !!loadError}
                className="flex h-10 w-10 items-center justify-center rounded-button bg-primary text-bg hover:bg-primary-hover transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5" />
                ) : (
                  <Play className="h-5 w-5 ml-0.5" />
                )}
              </button>

              <button
                data-testid="audio-player-skip-forward"
                onClick={skipForward}
                disabled={isLoading || !!loadError}
                className={cn(
                  CONTROL_SQUARE.sm,
                  'flex items-center justify-center rounded-button text-fg-muted hover:bg-bg-elevated hover:text-fg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                aria-label={t('accessibility.audioPlayer.skipForward')}
              >
                <SkipForward className="h-4 w-4" />
              </button>
            </div>

            {/* Time display */}
            <div
              data-testid="audio-player-time-display"
              className="text-sm text-fg-subtle font-mono"
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Playback speed */}
            <Select
              data-testid="audio-player-speed-select"
              value={String(playbackRate)}
              onValueChange={(val) => setPlaybackRate(Number(val))}
              disabled={isLoading}
              options={[
                { value: '0.5', label: '0.5x' },
                { value: '0.75', label: '0.75x' },
                { value: '1', label: '1x' },
                { value: '1.25', label: '1.25x' },
                { value: '1.5', label: '1.5x' },
                { value: '2', label: '2x' },
              ]}
              size="sm"
              className="w-[80px]"
            />

            {/* Volume */}
            <div
              data-testid="audio-player-volume-controls"
              className="flex items-center gap-2"
            >
              <button
                data-testid="audio-player-mute-button"
                onClick={toggleMute}
                disabled={isLoading || !!loadError}
                className={cn(
                  CONTROL_SQUARE.sm,
                  'flex items-center justify-center rounded-button text-fg-muted hover:bg-bg-elevated hover:text-fg transition-all duration-150 disabled:opacity-50'
                )}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </button>

              <input
                data-testid="audio-player-volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                disabled={isLoading || !!loadError}
                className="w-20"
                aria-label={t('accessibility.audioPlayer.volume')}
              />
            </div>
          </div>
        )}

        {activeSegment && (
          <button
            type="button"
            data-testid="audio-player-continue"
            onClick={continuePlayback}
            className="shrink-0 text-xs text-primary hover:text-primary-hover transition-colors"
          >
            {t('audioPlayer.continueRecording', 'Continue recording')}
          </button>
        )}
      </div>
    );
  }
);

AudioPlayer.displayName = 'AudioPlayer';

/**
 * Fixed-size stand-in rendered in the player's slot while the audio blob is
 * still downloading. Mirrors AudioPlayer's layout heights (80px waveform +
 * 40px controls in full mode, a single 32px row in compact mode) so the
 * player appearing later does NOT shift the transcript below it.
 */
export function AudioPlayerSkeleton({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <div
        data-testid="audio-player-skeleton"
        className={cn('flex items-center gap-3', className)}
      >
        <Skeleton className="h-8 w-8 shrink-0 rounded-button" />
        <Skeleton className="h-1.5 flex-1 rounded-full" />
        <Skeleton className="h-4 w-16 shrink-0" />
      </div>
    );
  }

  return (
    <div data-testid="audio-player-skeleton" className={cn('space-y-4', className)}>
      <Skeleton className="h-20 w-full rounded-card" />
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-32 rounded-button" />
        <Skeleton className="h-4 w-24" />
        <div className="flex-1" />
        <Skeleton className="h-8 w-36 rounded-button" />
      </div>
    </div>
  );
}
